// ---------------------------------------------------------------------------
// Auth service — owns the session (in-memory source of truth + best-effort
// sessionStorage persistence). The injected `verify` yields the whole session;
// this service persists it verbatim and never mints tokens, so the real
// `/api/login` integration is a drop-in swap of `verify` alone.
// ---------------------------------------------------------------------------

import { createLogger } from '../logging/logger.js';
import { isAbortError } from '../http/is-abort-error.js';

/**
 * @typedef {import('../../contracts/auth.js').AuthState} AuthState
 * @typedef {import('../../contracts/auth.js').UserInfo} UserInfo
 * @typedef {import('../../contracts/auth.js').SessionResult} SessionResult
 * @typedef {import('../../contracts/auth.js').VerifyFn} VerifyFn
 * @typedef {import('../../contracts/auth.js').LoginResult} LoginResult
 * @typedef {import('../../contracts/auth.js').AuthService} AuthService
 */

/**
 * @typedef {Object} StorageLike
 * @property {(key: string) => string | null} getItem
 * @property {(key: string, value: string) => void} setItem
 * @property {(key: string) => void} removeItem
 */

const STORAGE_KEY = 'adminsr_auth';

// Shared, deep-frozen (returned from getState() when logged out) — freezing
// keeps the "getState is immutable" invariant even in the unauthenticated case,
// and makes it safe to share this single instance across all services.
/** @type {AuthState} */
const UNAUTHENTICATED = Object.freeze({ authenticated: false, user: null, token: null });

/**
 * Resolve the default storage inside a try/catch: `window.sessionStorage` can
 * throw *on property access* (sandboxed iframe without `allow-same-origin` →
 * SecurityError), before any read guard runs. A `null` result degrades the
 * service to in-memory-only (still works, just won't survive a refresh).
 *
 * @param {StorageLike | undefined} injected
 * @returns {StorageLike | null}
 */
function resolveStorage(injected) {
  if (injected) return injected;
  try {
    return /** @type {StorageLike} */ (window.sessionStorage);
  } catch {
    return null;
  }
}

/**
 * Validate a candidate session (a stored record OR a `verify` result) against
 * the `UserInfo` contract. Used for both hydration and login-commit so a
 * malformed API response is rejected exactly like a malformed stored record.
 * `roles`/`permissions` are `string[]`, so every element must be a string —
 * `Array.isArray` alone would admit non-string entries.
 *
 * @param {unknown} record
 * @returns {record is SessionResult & { token: string }}
 */
function validateSession(record) {
  if (!record || typeof record !== 'object') return false;
  const r = /** @type {Record<string, unknown>} */ (record);
  if (typeof r['token'] !== 'string' || r['token'] === '') return false;

  const user = r['user'];
  if (!user || typeof user !== 'object') return false;
  const u = /** @type {Record<string, unknown>} */ (user);

  if (typeof u['id'] !== 'string' || u['id'] === '') return false;
  if (typeof u['email'] !== 'string' || u['email'] === '') return false;
  if (typeof u['name'] !== 'string' || u['name'] === '') return false;

  const isStringArray = (/** @type {unknown} */ v) =>
    Array.isArray(v) && v.every((x) => typeof x === 'string');
  if (!isStringArray(u['roles'])) return false;
  if (!isStringArray(u['permissions'])) return false;

  return true;
}

/**
 * Build a deep-frozen authenticated `AuthState` from a validated session, so
 * `getState()` callers can never mutate `user`/`roles`/`permissions`. State is
 * only ever replaced, never mutated, so freezing costs nothing.
 *
 * @param {SessionResult & { token: string }} session
 * @returns {AuthState}
 */
function freezeAuthenticated(session) {
  // Freeze at runtime for the immutability invariant; the cast keeps the public
  // type as the contract's mutable `string[]` (consumers only ever read these).
  const user = Object.freeze({
    id: session.user.id,
    email: session.user.email,
    name: session.user.name,
    roles: /** @type {string[]} */ (Object.freeze([...session.user.roles])),
    permissions: /** @type {string[]} */ (Object.freeze([...session.user.permissions])),
  });
  return Object.freeze({ authenticated: true, user, token: session.token });
}

/**
 * Create the auth service.
 *
 * @param {{ verify: VerifyFn, storage?: StorageLike, logger?: ReturnType<typeof createLogger> }} deps
 * @returns {AuthService}
 */
export function createAuthService({ verify, storage: injectedStorage, logger }) {
  const log = logger ?? createLogger({ module: 'auth-service' });
  const storage = resolveStorage(injectedStorage);

  // Read + parse + validate the stored record in one pass. Distinguishes the
  // three cases hydrate() cares about:
  //   'absent'  — no key / storage unavailable / unreadable (no warning)
  //   'invalid' — present but corrupt/partial (hydrate discards + warns)
  //   'valid'   — a well-formed session
  /** @returns {{ status: 'absent' } | { status: 'invalid' } | { status: 'valid', record: SessionResult & { token: string } }} */
  function readRecord() {
    if (!storage) return { status: 'absent' };
    /** @type {string | null} */
    let raw;
    try {
      raw = storage.getItem(STORAGE_KEY);
    } catch {
      return { status: 'absent' };
    }
    if (raw === null) return { status: 'absent' };
    try {
      const parsed = JSON.parse(raw);
      return validateSession(parsed) ? { status: 'valid', record: parsed } : { status: 'invalid' };
    } catch {
      return { status: 'invalid' };
    }
  }

  /** @param {SessionResult & { token: string }} record */
  function writeRecord(record) {
    if (!storage) return;
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      // Storage unavailable (private mode, quota) — session stays in-memory only.
    }
  }

  function removeRecord() {
    if (!storage) return;
    try {
      storage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore — nothing to clean up if storage is unavailable.
    }
  }

  /** @returns {AuthState} */
  function hydrate() {
    const read = readRecord();
    if (read.status === 'valid') return freezeAuthenticated(read.record);
    if (read.status === 'invalid') {
      // Present-but-corrupt — discard so it can't wedge the boot, and warn.
      removeRecord();
      log.warn('Discarded malformed stored session', { data: { key: STORAGE_KEY } });
    }
    return UNAUTHENTICATED;
  }

  /** @type {AuthState} */
  let state = hydrate();

  let epoch = 0;
  /** @type {AbortController | null} */
  let pendingController = null;

  // Invalidate any in-flight login: bump the epoch (stale-commit guard) AND
  // abort the pending request (cancels wasted work / a post-logout server hit).
  function invalidate() {
    epoch += 1;
    if (pendingController) {
      pendingController.abort();
      pendingController = null;
    }
  }

  return {
    getState() {
      return state;
    },

    async login(email, password) {
      invalidate();
      const opEpoch = epoch;
      const controller = new AbortController();
      pendingController = controller;

      log.info('Login start', { data: { email } });

      /** @type {SessionResult | null} */
      let result;
      try {
        result = await verify(email, password, { signal: controller.signal });
      } catch (err) {
        // A newer login/logout/teardown superseded this one while it was in flight.
        if (opEpoch !== epoch) {
          log.info('Login superseded', { data: { email } });
          return { ok: false, reason: 'cancelled' };
        }
        pendingController = null;
        if (isAbortError(err)) {
          // Aborted → cancellation, never an error toast (§5).
          return { ok: false, reason: 'cancelled' };
        }
        log.error('Login failed', { data: err });
        return { ok: false, reason: 'error' };
      }

      // Superseded after a successful settle — commit nothing.
      if (opEpoch !== epoch) {
        log.info('Login superseded', { data: { email } });
        return { ok: false, reason: 'cancelled' };
      }
      pendingController = null;

      if (result === null) {
        log.warn('Login rejected: invalid credentials', { data: { email } });
        return { ok: false, reason: 'invalid_credentials' };
      }

      if (!validateSession(result)) {
        log.error('Login failed: malformed session from verifier', { data: { email } });
        return { ok: false, reason: 'error' };
      }

      state = freezeAuthenticated(result);
      writeRecord(result);
      log.info('Login success', { data: { email: state.user?.email } });
      return { ok: true };
    },

    logout() {
      invalidate();
      state = UNAUTHENTICATED;
      removeRecord();
      log.info('Logout');
    },

    invalidatePending() {
      invalidate();
    },
  };
}
